// epgModel.js
// Turn the parsed XMLTV data model into what the EPG grid renders for one day:
// per-channel programme rows positioned along a horizontal timeline (a fixed
// 00:00-24:00 feed wall-clock), plus the list of days the feed covers and
// channel search filtering. All times are deterministic wall-clock values in
// the feed's clock, independent of the viewer's local timezone.

import { MINUTES_PER_DAY } from './parseEpg';

export const PIXELS_PER_MINUTE = 0.7;
export const CHANNEL_COL_WIDTH = 180;
export const MIN_BLOCK_PX = 32;

/**
 * Sorted list of distinct day keys (`YYYY-MM-DD`) the feed covers, derived from
 * each programme's start day (falling back to its stop day).
 */
export function getFeedDays(parsed) {
    const { programmes = [] } = parsed || {};
    return [...new Set(programmes.map((p) => p.startDay).filter(Boolean))].sort();
}

/**
 * Build the grid content for one day. Each row is a channel whose programmes
 * (clipped to the 00:00-24:00 window and laid out by start time) are positioned
 * in pixels along the timeline; programmes that do not overlap the day are
 * dropped. `timeTicks` are the hourly ruler marks for the day.
 *
 * @param {{channels: Array<{id: string, name: string}>,
 *          programmes: Array<object>}} parsed - Parsed XMLTV data model.
 * @param {string} dayKey - Feed day key (`YYYY-MM-DD`).
 * @returns {{widthPx: number, rows: Array<{channel: {id: string, name: string},
 *            programmes: Array<{title: string, description: string,
 *            startPx: number, widthPx: number, startLabel: string,
 *            stopLabel: string, startAbs: number}>}>,
 *            timeTicks: Array<{label: string, px: number}>}}
 */
export function buildDayView(parsed, dayKey) {
    const { channels = [], programmes = [] } = parsed || {};
    const dayStartAbs = Date.UTC(
        parseInt(dayKey.slice(0, 4), 10),
        parseInt(dayKey.slice(5, 7), 10) - 1,
        parseInt(dayKey.slice(8, 10), 10),
    ) / 60000;
    const dayEndAbs = dayStartAbs + MINUTES_PER_DAY;
    const widthPx = MINUTES_PER_DAY * PIXELS_PER_MINUTE;
    const minutesToPx = (abs) => (abs - dayStartAbs) * PIXELS_PER_MINUTE;
    const formatLabel = (abs) => {
        const date = new Date(abs * 60000);
        const h = String(date.getUTCHours()).padStart(2, '0');
        const m = String(date.getUTCMinutes()).padStart(2, '0');
        return `${h}:${m}`;
    };

    const rows = channels.map((channel) => {
        const rowProgrammes = programmes
            .filter((programme) => programme.channelId === channel.id)
            .filter(
                (programme) => programme.startAbs < dayEndAbs && programme.stopAbs > dayStartAbs,
            )
            .map((programme) => {
                const clippedStart = Math.max(programme.startAbs, dayStartAbs);
                const clippedStop = Math.min(programme.stopAbs, dayEndAbs);
                return {
                    title: programme.title,
                    description: programme.description,
                    startAbs: programme.startAbs,
                    startPx: minutesToPx(clippedStart),
                    stopPx: minutesToPx(clippedStop),
                    startLabel: formatLabel(programme.startAbs),
                    stopLabel: formatLabel(programme.stopAbs),
                };
            })
            .sort((a, b) => a.startAbs - b.startAbs)
            .map((programme) => ({
                ...programme,
                widthPx: Math.max(programme.stopPx - programme.startPx, MIN_BLOCK_PX),
            }));
        return { channel, programmes: rowProgrammes };
    });

    const timeTicks = [];
    for (let hour = 0; hour < 24; hour += 1) {
        timeTicks.push({
            label: `${String(hour).padStart(2, '0')}:00`,
            px: hour * 60 * PIXELS_PER_MINUTE,
        });
    }

    return { widthPx, rows, timeTicks };
}

/**
 * Filter channels by a case-insensitive substring of the channel name or id.
 * An empty query returns every channel.
 */
export function searchChannels(parsed, query) {
    const { channels = [] } = parsed || {};
    const needle = (query || '').trim().toLowerCase();
    if (!needle) {
        return channels;
    }
    return channels.filter((channel) =>
        [channel.name, channel.id].some(
            (text) => (text || '').toLowerCase().includes(needle),
        ),
    );
}

/**
 * Human-friendly label for a feed day key, e.g. `Monday, September 18, 2026`,
 * formatted in the viewer's local timezone (label only - no time math).
 */
export function formatDayLabel(dayKey) {
    const year = parseInt(dayKey.slice(0, 4), 10);
    const month = parseInt(dayKey.slice(5, 7), 10) - 1;
    const day = parseInt(dayKey.slice(8, 10), 10);
    const date = new Date(year, month, day);
    if (Number.isNaN(date.getTime())) {
        return dayKey;
    }
    return date.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}
